import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import quickbooksService, {
  formatCurrencyPrecise,
  formatDate,
  getEntityColor,
} from '../../services/quickbooksService';
import creditNotesService from '../../services/creditNotesService';

const emailStatusColor = (s) => (s === 'EmailSent' ? 'success' : 'default');
const printStatusColor = (s) => (s === 'PrintComplete' ? 'success' : 'default');

export default function CreditNotes() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');

  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customer, setCustomer] = useState('');
  const [currency, setCurrency] = useState('all');

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (selectedEntity !== 'all') params.connector_id = selectedEntity;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (customer.trim()) params.customer = customer.trim();
    if (currency !== 'all') params.currency = currency;
    return params;
  }, [selectedEntity, startDate, endDate, customer, currency]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = buildParams();

      const entitiesRes = await quickbooksService.getQuickBooksEntities();
      if (entitiesRes.success) setEntities(entitiesRes.data || []);

      const [summaryRes, listRes] = await Promise.all([
        creditNotesService.getCreditNotesSummary(params),
        creditNotesService.getCreditNotes({ ...params, limit: 500 }),
      ]);

      if (summaryRes.success) setSummary(summaryRes.data);
      if (listRes.success) setRows(listRes.data.credit_notes || []);
      setPage(0);
    } catch (err) {
      console.error('Error loading credit notes:', err);
      setError('Failed to load credit notes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  // Auto-fetch on mount and when the entity changes. Date/customer/currency
  // filters are applied deliberately via the Apply button (or Enter in the
  // customer box) so we don't refetch on every keystroke.
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await creditNotesService.getCreditNoteDetail(row.id, { connector_id: row.connector_id });
      if (res.success) setDetail(res.data);
    } catch (err) {
      console.error('Error loading credit note detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await creditNotesService.downloadCreditNotesCsv(buildParams());
    } catch (err) {
      console.error('Error exporting CSV:', err);
    }
  };

  const currencyOptions = (summary?.by_currency || []).map((c) => c.currency_code);

  const KPICard = ({ title, value, subtitle, icon, color = 'primary.main' }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {icon && <Box sx={{ color }}>{icon}</Box>}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon fontSize="large" /> Credit Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Credit memos issued to customers / retailers, synced from QuickBooks
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select value={selectedEntity} label="Entity" onChange={(e) => setSelectedEntity(e.target.value)}>
              <MenuItem value="all">All entities</MenuItem>
              {entities.map((e) => (
                <MenuItem key={e.connector_id} value={e.connector_id}>
                  {e.entity_name || e.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Export CSV">
            <span>
              <IconButton onClick={handleExport} disabled={loading || rows.length === 0}>
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              label="From" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={startDate} onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              label="To" type="date" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={endDate} onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Customer" size="small" fullWidth placeholder="Search customer…"
              value={customer} onChange={(e) => setCustomer(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') loadData(); }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel>Currency</InputLabel>
              <Select value={currency} label="Currency" onChange={(e) => setCurrency(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                {currencyOptions.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button variant="contained" fullWidth onClick={loadData}>Apply</Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <KPICard title="Total Credit Notes" value={summary?.total_count ?? '—'} icon={<ReceiptLongIcon />} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KPICard title="Customers" value={summary?.total_customers ?? '—'} icon={<PeopleIcon />} />
        </Grid>
        {(summary?.by_currency || []).map((c) => (
          <Grid item xs={6} md={3} key={c.currency_code}>
            <KPICard
              title={`${c.currency_code} · ${c.count} notes`}
              value={formatCurrencyPrecise(c.total_amount, c.currency_code)}
              subtitle={`Remaining ${formatCurrencyPrecise(c.remaining_credit, c.currency_code)}`}
            />
          </Grid>
        ))}
      </Grid>

      {/* Table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No credit notes found for the selected filters.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Doc #</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Remaining</TableCell>
                    <TableCell align="right">Tax</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => (
                    <TableRow
                      key={`${r.connector_id}-${r.id}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openDetail(r)}
                    >
                      <TableCell>{formatDate(r.txn_date)}</TableCell>
                      <TableCell>{r.doc_number || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.entity_name}
                          size="small"
                          sx={{ bgcolor: getEntityColor(r.entity_name), color: '#fff' }}
                        />
                      </TableCell>
                      <TableCell>{r.customer_ref_name || '—'}</TableCell>
                      <TableCell align="right">{formatCurrencyPrecise(r.total_amount, r.currency_code)}</TableCell>
                      <TableCell align="right">{formatCurrencyPrecise(r.remaining_credit, r.currency_code)}</TableCell>
                      <TableCell align="right">{formatCurrencyPrecise(r.total_tax_amount, r.currency_code)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {r.email_status && r.email_status !== 'NotSet' && (
                            <Chip label={r.email_status} size="small" color={emailStatusColor(r.email_status)} variant="outlined" />
                          )}
                          {r.print_status && (
                            <Chip label={r.print_status} size="small" color={printStatusColor(r.print_status)} variant="outlined" />
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Detail drawer */}
      <Drawer anchor="right" open={detailOpen} onClose={() => setDetailOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Credit Note {detail?.doc_number ? `#${detail.doc_number}` : ''}</Typography>
            <IconButton onClick={() => setDetailOpen(false)}><CloseIcon /></IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
          ) : detail ? (
            <>
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <DetailField label="Customer" value={detail.customer_ref_name} />
                <DetailField label="Entity" value={detail.entity_name} />
                <DetailField label="Date" value={formatDate(detail.txn_date)} />
                <DetailField label="Currency" value={`${detail.currency_code || ''}${detail.exchange_rate && detail.exchange_rate !== 1 ? ` @ ${detail.exchange_rate}` : ''}`} />
                <DetailField label="Total" value={formatCurrencyPrecise(detail.total_amount, detail.currency_code)} />
                <DetailField label="Tax" value={formatCurrencyPrecise(detail.total_tax_amount, detail.currency_code)} />
                <DetailField label="Remaining credit" value={formatCurrencyPrecise(detail.remaining_credit, detail.currency_code)} />
                {detail.global_tax_calculation && <DetailField label="Tax basis" value={detail.global_tax_calculation} />}
                {detail.department_ref_name && <DetailField label="Department" value={detail.department_ref_name} />}
              </Grid>
              {detail.customer_memo && (
                <Alert severity="info" sx={{ mb: 2 }}>{detail.customer_memo}</Alert>
              )}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Line items</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.line_items || []).map((l, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          {l.item_name || l.description || '—'}
                          {l.class_name && (
                            <Typography variant="caption" display="block" color="text.secondary">{l.class_name}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{l.qty || '—'}</TableCell>
                        <TableCell align="right">{formatCurrencyPrecise(l.unit_price, detail.currency_code)}</TableCell>
                        <TableCell align="right">{formatCurrencyPrecise(l.amount, detail.currency_code)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography color="text.secondary">No detail available.</Typography>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

function DetailField({ label, value }) {
  return (
    <Grid item xs={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
    </Grid>
  );
}
